import React from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Handle,
  Position,
} from '@xyflow/react';
import type { Node, Edge, NodeTypes, NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

/* ── Visual constants (matching real app) ─────────────────── */

const COLORS: Record<string, string> = {
  prompt: '#7ea8c9',
  media: '#b5a0cc',
  generate: '#3e4557',
  output: '#b5a0cc',
  variation: '#b5a0cc',
};

const ICONS: Record<string, React.ReactNode> = {
  prompt: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  media: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  generate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5z" />
    </svg>
  ),
  output: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  variation: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v4m0 12v4m-7.07-15.07l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4" />
    </svg>
  ),
};

const GRADIENTS: Record<string, string> = {
  warm: 'linear-gradient(135deg, #f0e8d8 0%, #e8d4c0 50%, #ddc8b0 100%)',
  cool: 'linear-gradient(135deg, #d8e4f0 0%, #c8d4e8 50%, #d0dce8 100%)',
  mix: 'linear-gradient(135deg, #e0d8ef 0%, #d4e2f0 50%, #d8ede4 100%)',
  brand: 'linear-gradient(135deg, #e8e0d0 0%, #e0d8c8 40%, #d8d0c0 100%)',
  purple: 'linear-gradient(135deg, #e0d0f0 0%, #d8c8e8 50%, #e8d8f0 100%)',
};

/* ══════════════════════════════════════════════════════════════
   MiniNode — Custom React Flow node matching app card style
   ══════════════════════════════════════════════════════════════ */

function MiniNode({ data }: NodeProps) {
  const d = data as Record<string, any>;
  const nt: string = d.nodeType || 'prompt';
  const color = COLORS[nt] || COLORS.prompt;
  const label = d.label || nt.charAt(0).toUpperCase() + nt.slice(1);

  return (
    <div
      className="bg-white rounded-[10px] border border-black/[0.08] shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
      style={{ width: d.width || 120 }}
    >
      {/* Left handle (target) */}
      {d.handleLeft && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-[5px] !h-[5px] !min-w-0 !min-h-0 !border !border-white !shadow-[0_0_2px_rgba(0,0,0,0.12)]"
          style={{ background: color }}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-[5px] px-2 py-[5px] border-b border-black/[0.04] bg-[rgba(252,251,248,0.9)] rounded-t-[10px]">
        <span className="w-3 h-3 flex-shrink-0" style={{ color }}>
          {ICONS[nt]}
        </span>
        <span className="text-[9px] font-semibold" style={{ color }}>
          {label}
        </span>
        {d.badge && (
          <span className="ml-auto text-[7px] px-[5px] py-[1px] rounded-[3px] bg-black/[0.05] text-[#8c95a6]">
            {d.badge}
          </span>
        )}
      </div>

      {/* Body text */}
      {d.body && (
        <div className="px-[9px] py-[7px] text-[9px] leading-[1.45] text-[#1d2939]/80">
          {d.body}
        </div>
      )}

      {/* Image area — real image or gradient fallback */}
      {(d.imageSrc || d.image) && (
        <div
          className={`w-full overflow-hidden${!d.filename && !d.footerLines ? ' rounded-b-[10px]' : ''}`}
          style={{ aspectRatio: d.imageRatio || '1' }}
        >
          {d.imageSrc ? (
            <img
              src={d.imageSrc}
              alt={d.filename || ''}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{ background: GRADIENTS[d.image] || GRADIENTS.mix }}
            />
          )}
        </div>
      )}

      {/* Filename */}
      {d.filename && (
        <div className="px-2 py-[4px] text-[7.5px] text-[#8c95a6]">{d.filename}</div>
      )}

      {/* Footer */}
      {d.footerLines && (
        <div className="px-2 py-[5px] border-t border-black/[0.04]">
          {(d.footerLines as string[]).map((line: string, i: number) => (
            <div
              key={i}
              className={
                i === 1
                  ? 'text-[8.5px] font-semibold text-[#1d2939] mt-[2px]'
                  : 'text-[7.5px] text-[#8c95a6] mt-[1px]'
              }
            >
              {line}
            </div>
          ))}
        </div>
      )}

      {/* Right handle (source) */}
      {d.handleRight && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-[5px] !h-[5px] !min-w-0 !min-h-0 !border !border-white !shadow-[0_0_2px_rgba(0,0,0,0.12)]"
          style={{ background: color }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   NoteNode — Yellow sticky note
   ══════════════════════════════════════════════════════════════ */

function NoteNode({ data }: NodeProps) {
  const d = data as Record<string, any>;
  return (
    <div
      className="bg-[#fef9c3] border border-[rgba(200,180,80,0.2)] rounded-[4px] px-2 py-[6px] text-[8px] italic text-[#92750a] leading-[1.4] shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
      style={{
        transform: `rotate(${d.rotation || -1}deg)`,
        width: d.width || 120,
      }}
    >
      {d.text as string}
    </div>
  );
}

/* ── Node types registry ─────────────────────────────────── */

const nodeTypes: NodeTypes = {
  mini: MiniNode,
  note: NoteNode,
};

/* ══════════════════════════════════════════════════════════════
   DemoFlow — Non-interactive React Flow wrapper for demos
   ══════════════════════════════════════════════════════════════ */

export function DemoFlow({
  nodes,
  edges,
  className = '',
  style,
  bgGap = 18,
  bgOpacity = 0.12,
  fitPadding = 0.1,
}: {
  nodes: Node[];
  edges: Edge[];
  className?: string;
  style?: React.CSSProperties;
  bgGap?: number;
  bgOpacity?: number;
  fitPadding?: number;
}) {
  return (
    <div className={`lp-flow ${className}`} style={style}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: fitPadding }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{
            type: 'default',
            animated: true,
            style: { strokeWidth: 1.2 },
          }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={bgGap}
            size={1}
            color={`rgba(62,69,87,${bgOpacity})`}
          />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

/* ── Edge helper ─────────────────────────────────────────── */

export function dEdge(
  id: string,
  source: string,
  target: string,
  color = '#7ea8c9',
): Edge {
  return {
    id,
    source,
    target,
    type: 'default',
    animated: true,
    style: { stroke: color, strokeWidth: 1.2 },
  };
}

/* ══════════════════════════════════════════════════════════════
   MobileDemoFlow — Static vertical card stack for mobile
   Mirrors the hero mobile pattern: cards stacked with edges
   ══════════════════════════════════════════════════════════════ */

export interface MobileNodeItem {
  nodeType: string;
  label?: string;
  badge?: string;
  body?: string;
  image?: string;
  imageSrc?: string;
  imageRatio?: string;
  filename?: string;
  footerLines?: string[];
  /** Color of the edge leading INTO this node (skipped for first) */
  edgeColor?: string;
}

function MobileEdgeLine({ color }: { color: string }) {
  return (
    <div className="flex justify-center h-7 my-[-2px]">
      <svg viewBox="0 0 20 28" fill="none" className="w-5 h-7">
        <path d="M10,0 L10,28" stroke={color} strokeWidth="1.5" opacity="0.35" strokeDasharray="4 3">
          <animate attributeName="stroke-dashoffset" from="0" to="-14" dur="2s" repeatCount="indefinite" />
        </path>
        <circle r="2.5" fill={color} opacity="0.7">
          <animateMotion dur="2.5s" repeatCount="indefinite" path="M10,0 L10,28" />
        </circle>
      </svg>
    </div>
  );
}

function MobileNodeCard({ node }: { node: MobileNodeItem }) {
  const nt = node.nodeType;
  const color = COLORS[nt] || COLORS.prompt;
  const label = node.label || nt.charAt(0).toUpperCase() + nt.slice(1);

  return (
    <div className="bg-white rounded-[10px] border border-black/[0.08] shadow-[0_2px_8px_rgba(0,0,0,0.05)] w-full max-w-[200px]">
      {/* Header */}
      <div className="flex items-center gap-[6px] px-2.5 py-[6px] border-b border-black/[0.04] bg-[rgba(252,251,248,0.9)] rounded-t-[10px]">
        <span className="w-3.5 h-3.5 flex-shrink-0" style={{ color }}>
          {ICONS[nt]}
        </span>
        <span className="text-[10px] font-semibold" style={{ color }}>
          {label}
        </span>
        {node.badge && (
          <span className="ml-auto text-[8px] px-[5px] py-[1px] rounded-[3px] bg-black/[0.05] text-[#8c95a6]">
            {node.badge}
          </span>
        )}
      </div>

      {/* Body */}
      {node.body && (
        <div className="px-2.5 py-[8px] text-[10px] leading-[1.45] text-[#1d2939]/80">
          {node.body}
        </div>
      )}

      {/* Image area — real image or gradient fallback */}
      {(node.imageSrc || node.image) && (
        <div
          className={`w-full overflow-hidden${!node.filename && !node.footerLines ? ' rounded-b-[10px]' : ''}`}
          style={{ aspectRatio: node.imageRatio || '1' }}
        >
          {node.imageSrc ? (
            <img
              src={node.imageSrc}
              alt={node.filename || ''}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{ background: GRADIENTS[node.image!] || GRADIENTS.mix }}
            />
          )}
        </div>
      )}

      {/* Filename */}
      {node.filename && (
        <div className="px-2.5 py-[4px] text-[8px] text-[#8c95a6]">{node.filename}</div>
      )}

      {/* Footer */}
      {node.footerLines && (
        <div className="px-2.5 py-[5px] border-t border-black/[0.04]">
          {node.footerLines.map((line, i) => (
            <div
              key={i}
              className={
                i === 1
                  ? 'text-[9px] font-semibold text-[#1d2939] mt-[2px]'
                  : 'text-[8px] text-[#8c95a6] mt-[1px]'
              }
            >
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MobileDemoFlow({
  items,
  className = '',
}: {
  items: MobileNodeItem[];
  className?: string;
}) {
  return (
    <div
      className={`rounded-[14px] border border-black/[0.06] overflow-hidden ${className}`}
      style={{
        background: '#fafbfd',
        backgroundImage:
          'radial-gradient(circle, rgba(62,69,87,0.12) 1px, transparent 1px)',
        backgroundSize: '18px 18px',
      }}
    >
      <div className="flex flex-col items-center py-5 px-4">
        {items.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && item.edgeColor && (
              <MobileEdgeLine color={item.edgeColor} />
            )}
            <MobileNodeCard node={item} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
